import { faArrowDown, faArrowUp, faBook, faLayerGroup, faPhotoFilm, faScroll, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import ReferenceSelector from '../../../../components/ReferenceSelector';
import ReferencesSectionSelector from '../../../../components/ReferencesSectionSelector';
import AnnouncementSelector from '../../../../components/AnnouncementSelector';
import ImageSelector from './ImageSelector';
import AttachmentGroupSelector from './AttachmentGroupSelector';

type ContentType = 'reference' | 'references_section' | 'attachment' | 'attachments_group' | 'announcement';

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  reference: 'Reference',
  references_section: 'References Section',
  attachment: 'Attachment',
  attachments_group: 'Attachments Group',
  announcement: 'Announcement',
};

function FeaturedContentRow({
  index,
  contentType,
  onDelete,
  onMove,
  isFirst,
  isLast,
  animationDirection
}: {
  index: number;
  contentType: ContentType;
  onDelete: () => void;
  onMove: (direction: 'up' | 'down') => void;
  isFirst: boolean;
  isLast: boolean;
  animationDirection?: 'up' | 'down' | null;
}) {
  const fieldName = `featured_content.${index}.selected_record`;

  const renderSelector = () => {
    switch (contentType) {
      case 'reference':
        return <ReferenceSelector name={fieldName} />;
      case 'references_section':
        return <ReferencesSectionSelector name={fieldName} />;
      case 'attachment':
        return <ImageSelector name={fieldName} />;
      case 'attachments_group':
        return <AttachmentGroupSelector name={fieldName} />;
      case 'announcement':
        return <AnnouncementSelector name={fieldName} />;
      default:
        return null;
    }
  };

  const getAnimationStyle = () => {
    if (!animationDirection) return {};
    return {
      animation: `slide-${animationDirection} 0.3s ease-out`,
    };
  };

  return (
    <div
      style={{
        marginBottom: '8px',
        backgroundColor: 'var(--color-base-100)',
        border: '2px solid var(--color-base-300)',
        ...getAnimationStyle(),
      }}
    >
      <div
        style={{
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          backgroundColor: 'var(--color-base-200)',
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {renderSelector()}
        </div>

        <div
          style={{ display: 'flex', gap: '4px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => onMove('up')}
            disabled={isFirst}
            title="Move up"
          >
            <FontAwesomeIcon icon={faArrowUp} />
          </button>

          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => onMove('down')}
            disabled={isLast}
            title="Move down"
          >
            <FontAwesomeIcon icon={faArrowDown} />
          </button>

          <button
            type="button"
            className="btn btn-sm btn-error"
            onClick={onDelete}
            title="Delete"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// CSS keyframes for animation - injected once
const styleId = 'featured-content-animations';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes slide-up {
      0% { transform: translateY(100%); opacity: 0.7; }
      100% { transform: translateY(0); opacity: 1; }
    }
    @keyframes slide-down {
      0% { transform: translateY(-100%); opacity: 0.7; }
      100% { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

export default function FeaturedContentEditor() {
  const { control, watch } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "featured_content"
  });

  // Track which items are animating: { [fieldId]: 'up' | 'down' }
  const [animatingItems, setAnimatingItems] = useState<Record<string, 'up' | 'down'>>({});

  const addContent = (type: ContentType) => {
    append({
      content_type: type,
      selected_record: null
    });
  };

  if (fields.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: 'var(--color-base-content)' }}>
        <p style={{ marginBottom: '12px', opacity: 0.7 }}>No featured content yet</p>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => addContent('reference')}
          >
            <FontAwesomeIcon icon={faBook} />Reference
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => addContent('references_section')}
          >
            <FontAwesomeIcon icon={faFolderOpen} />References Section
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => addContent('attachment')}
          >
            <FontAwesomeIcon icon={faPhotoFilm} />Attachment
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => addContent('attachments_group')}
          >
            <FontAwesomeIcon icon={faLayerGroup} />Attachments Group
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => addContent('announcement')}
          >
            <FontAwesomeIcon icon={faScroll} />Announcement
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {fields.map((field, index) => {
        const contentType = watch(`featured_content.${index}.content_type`) as ContentType;
        return (
          <FeaturedContentRow
            key={field.id}
            index={index}
            contentType={contentType}
            onDelete={() => remove(index)}
            onMove={(direction) => {
              if (direction === 'up' && index > 0) {
                const otherFieldId = fields[index - 1].id;
                setAnimatingItems({
                  [field.id]: 'up',
                  [otherFieldId]: 'down',
                });
                move(index, index - 1);
                setTimeout(() => setAnimatingItems({}), 300);
              } else if (direction === 'down' && index < fields.length - 1) {
                const otherFieldId = fields[index + 1].id;
                setAnimatingItems({
                  [field.id]: 'down',
                  [otherFieldId]: 'up',
                });
                move(index, index + 1);
                setTimeout(() => setAnimatingItems({}), 300);
              }
            }}
            isFirst={index === 0}
            isLast={index === fields.length - 1}
            animationDirection={animatingItems[field.id] || null}
          />
        );
      })}

      <div style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => addContent('reference')}
        >
          <FontAwesomeIcon icon={faBook} />Reference
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => addContent('references_section')}
        >
          <FontAwesomeIcon icon={faFolderOpen} />References Section
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => addContent('attachment')}
        >
          <FontAwesomeIcon icon={faPhotoFilm} />Attachment
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => addContent('attachments_group')}
        >
          <FontAwesomeIcon icon={faLayerGroup} />Attachments Group
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => addContent('announcement')}
        >
          <FontAwesomeIcon icon={faScroll} />Announcement
        </button>
      </div>
    </div>
  );
}
